import { useState } from "react";
import ReactGA from "react-ga4";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useCurrentUser, useLogout } from "utils/hooks";
import { formatInput } from "utils/helpers";
import useFetchOrganizations from "./useFetchOrganizations";
import { updateOrganizationAction } from "store/actions/organizations";
import { Organization } from "types";

const useUpdateOrganization = (
  organizationId: string | undefined,
  organization?: Organization
) => {
  const [isUpdateSuccessful, setIsUpdateSuccessful] = useState(false);
  const dispatch = useDispatch();
  const { logOut } = useLogout();
  const { fetchOrganizations } = useFetchOrganizations();
  const { user } = useCurrentUser();

  const submitUpdateOrganization = (args: any) => {
    if (!args || !organizationId) {
      return null;
    }

    return new Promise<void>((resolve) => {
      setTimeout(() => {
        fetch(`${process.env.REACT_APP_API_URL}`, {
          method: "POST",
          credentials: "include",
          body: JSON.stringify({
            query: `
              mutation {
                updateOrganization(organization: {
                  uuid: "${organizationId}",
                  name: "${formatInput(args.practiceName || args.name)}",
                  description: "${formatInput(args.description)}",
                  location: "${formatInput(args.location)}",
                  slug: "${organization?.slug || organizationId}",
                  address1: "${formatInput(args.address1)}",
                  address2: "${formatInput(args.address2)}",
                  state: "${formatInput(args.state?.value || args.state)}",
                  zipcode: "${formatInput(args.zipcode)}",
                  phone: "${formatInput(args.phone)}",
                  treatmentType: ${
                    args.treatmentType
                      ? JSON.stringify(
                          args.treatmentType.map((t: any) => t.value)
                        )
                      : "null"
                  },
                  practiceSize: "${formatInput(
                    args.practiceSize?.value || args.practiceSize
                  )}",
                  logoId: "${args.logoId || organization?.logoId || ""}",
                  logoUrl: "${args.logoUrl || organization?.logoUrl || ""}"
                }) {
                  uuid
                  name
                  description
                  slug
                  location
                  address1
                  address2
                  state
                  zipcode
                  phone
                  treatmentType
                  practiceSize
                  logoId
                  logoUrl
                }
              }
            `,
          }),
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            organizationId: user?.activeOrganizationId || "NA",
            permissionKey: "update_organizations",
          },
        })
          .then(async (res) => {
            if (res.status !== 200 && res.status !== 201) {
              const error = await res.json();
              if (error.errors[0].message.includes("BRKN_6001")) {
                toast.warn(`For security purposes please log in again.`);
                logOut();
              } else {
                toast.error(
                  `${error.errors[0].message || "Hmm, something went wrong."}`
                );
                throw new Error("Failed!");
              }
            }
            return res.json();
          })
          .then((resData) => {
            if (resData.errors) {
              toast.error(
                `Hmm, something went wrong. ${resData.errors[0].message}`
              );
              resolve();
              return true;
            } else {
              fetchOrganizations();
              toast("Your org has been updated");
              setIsUpdateSuccessful(true);
              resolve();
              ReactGA.event("organization_updated_success", {
                user_id: user?.id,
                organization_id: organizationId,
              });
              return dispatch(
                updateOrganizationAction(resData.data.updateOrganization)
              );
            }
          })
          .catch((err) => {
            console.log(err);
          });
      }, 100);
    });
  };

  return {
    submitUpdateOrganization,
    isUpdateSuccessful,
  };
};

export default useUpdateOrganization;
